import { useState, useEffect, useRef } from "react";
import DoctorHomeDiary from "./DoctorHomeDiary";
import DoctorHomeDiarySkeleton from "./DoctorHomeDiarySkeleton";
import { getAllPatients } from "../../API/PatientDirectoryCalls";
import {
  getAllStudyIDs,
  getAllStudyInformation,
  getStudyDashboardData,
} from "../../API/HomeDiaryCalls";
import { withTimeout } from "../../utils/apiTimeout";

type Study = {
  abstract: String;
  name: String;
  patients: {
    $id: String;
    $values: Array<any>;
  };
  studyID: Number;
};

type LazyDoctorHomeDiaryProps = {
  cachedData?: {
    data: {
      patients: any[];
      studies: Study[];
      dashboardData: any;
    };
    timestamp: number;
  };
  onDataLoaded?: (data: {
    patients: any[];
    studies: Study[];
    dashboardData: any;
  }) => void;
};

const CACHE_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes

function LazyDoctorHomeDiary({ cachedData, onDataLoaded }: LazyDoctorHomeDiaryProps) {
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patientsList, setPatientsList] = useState<any[]>(
    cachedData?.data?.patients || []
  );
  const [allStudyData, setAllStudyData] = useState<Study[]>(
    cachedData?.data?.studies || []
  );
  const [dashBoardStudyData, setDashBoardStudyData] = useState<any>(
    cachedData?.data?.dashboardData || {
      currentlyActiveStudy: 0,
      registeredPatients: 0,
      studies: {},
    }
  );

  const patientsListRef = useRef(patientsList);
  useEffect(() => { patientsListRef.current = patientsList; }, [patientsList]);

  const allStudyDataRef = useRef(allStudyData);
  useEffect(() => { allStudyDataRef.current = allStudyData; }, [allStudyData]);

  const dashBoardStudyDataRef = useRef(dashBoardStudyData);
  useEffect(() => { dashBoardStudyDataRef.current = dashBoardStudyData; }, [dashBoardStudyData]);

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);

  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;

    const loadData = async () => {
      // Check if cache is still fresh (less than 5 minutes old)
      const isCacheFresh = cachedData && 
        (Date.now() - cachedData.timestamp) < CACHE_VALIDITY_MS;

      if (isCacheFresh) {
        // Cache is fresh, use it without refreshing
        setPatientsList(cachedData.data.patients || []);
        setAllStudyData(cachedData.data.studies || []);
        setDashBoardStudyData(cachedData.data.dashboardData || {
          currentlyActiveStudy: 0,
          registeredPatients: 0,
          studies: {},
        });
        setIsLoading(false);
        return;
      }

      // If we have cached data (but it's stale), show it immediately
      // and refresh in the background
      if (cachedData) {
        setPatientsList(cachedData.data.patients || []);
        setAllStudyData(cachedData.data.studies || []);
        setDashBoardStudyData(cachedData.data.dashboardData || {
          currentlyActiveStudy: 0,
          registeredPatients: 0,
          studies: {},
        });
        setIsLoading(false);
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        setError(null);

        // Load independent data in parallel with timeout
        const [patients, studyIDs] = await Promise.all([
          withTimeout(getAllPatients()),
          withTimeout(getAllStudyIDs()),
        ]);

        // Check if still mounted before updating state
        if (!isMounted.current) return;

        // Only update if data actually changed
        if (JSON.stringify(patients) !== JSON.stringify(patientsListRef.current)) {
          setPatientsList(patients || []);
        }

        // Load studies info (depends on studyIDs)
        let studiesData: Study[] = [];
        if (studyIDs !== undefined && studyIDs.length > 0) {
          try {
            const studiesResponse = await withTimeout(
              getAllStudyInformation(studyIDs)
            );
            if (studiesResponse.$values) {
              studiesData = studiesResponse.$values;
            }
          } catch (error) {
            console.error("Error loading study information:", error);
            // Keep using cached studies data on error
            if (cachedData) {
              studiesData = cachedData.data.studies || [];
            }
          }
        }

        // Check if still mounted
        if (!isMounted.current) return;

        // Only update if data actually changed
        if (JSON.stringify(studiesData) !== JSON.stringify(allStudyDataRef.current)) {
          setAllStudyData(studiesData);
        }

        // Load dashboard data with timeout
        try {
          const dashboardData = await withTimeout(getStudyDashboardData());

          // Check if still mounted
          if (!isMounted.current) return;

          const newDashboardData = dashboardData || {
            currentlyActiveStudy: 0,
            registeredPatients: 0,
            studies: {},
          };

          // Only update if data actually changed
          if (JSON.stringify(newDashboardData) !== JSON.stringify(dashBoardStudyDataRef.current)) {
            setDashBoardStudyData(newDashboardData);
          }

          // Notify parent component that data is loaded (for caching)
          if (onDataLoaded && isMounted.current) {
            onDataLoaded({
              patients: patients || [],
              studies: studiesData,
              dashboardData: dashboardData || {},
            });
          }
        } catch (error) {
          console.error("Error loading dashboard data:", error);
          // Keep using cached data if available
          if (cachedData) {
            return;
          }
        }
      } catch (error) {
        console.error("Error loading Home Diary data:", error);

        // If we have cached data, keep using it despite the error
        if (cachedData) {
          console.log("Using cached data despite refresh error");
          return;
        }

        // Only show error if we have no data at all and still mounted
        if (isMounted.current) {
          setError(
            error instanceof Error && error.message.includes("timed out")
              ? "Loading took too long. Please refresh the page."
              : "Failed to load data. Please try again."
          );
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    loadData();

    // Cleanup function - runs when component unmounts
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedData, onDataLoaded]);

  const handleStudyChange = (newStudy: Study) => {
    setAllStudyData([...allStudyData, newStudy]);
  };

  const refreshDoctorDashboardData = async () => {
    try {
      const patients = await withTimeout(getAllPatients());
      if (isMounted.current) {
        setPatientsList(patients || []);
      }

      let studiesData: Study[] = [];
      const studyIDs = await withTimeout(getAllStudyIDs());
      if (studyIDs !== undefined && studyIDs.length > 0) {
        const studiesResponse = await withTimeout(
          getAllStudyInformation(studyIDs)
        );
        if (studiesResponse.$values) {
          studiesData = studiesResponse.$values;
          if (isMounted.current) {
            setAllStudyData(studiesData);
          }
        }
      }

      const dashboardData = await withTimeout(getStudyDashboardData());
      if (isMounted.current) {
        setDashBoardStudyData(dashboardData || {
          currentlyActiveStudy: 0,
          registeredPatients: 0,
          studies: {},
        });

        // Update cache after refresh
        if (onDataLoaded) {
          onDataLoaded({
            patients: patients || [],
            studies: studiesData,
            dashboardData: dashboardData || {},
          });
        }
      }
    } catch (error) {
      console.error("Error refreshing dashboard data:", error);
    }
  };

  if (isLoading) {
    return <DoctorHomeDiarySkeleton />;
  }

  if (error) {
    return (
      <div className="loadingContainer">
        <div className="body-text-size body-text">{error}</div>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  return (
    <>
      {isRefreshing && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '10px 16px',
          background: '#2196F3',
          color: 'white',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          🔄 Refreshing data...
        </div>
      )}
      <DoctorHomeDiary
        patients={patientsList}
        allStudyData={allStudyData}
        handleStudyChange={handleStudyChange}
        setAllStudyData={setAllStudyData}
        dashBoardStudyData={dashBoardStudyData}
        onDataRefreshNeeded={refreshDoctorDashboardData}
      />
    </>
  );
}

export default LazyDoctorHomeDiary;
